import { compact, map } from 'lodash';
import { formatDate, future, tz, utc } from '../../util/datetime';
import { toUUID } from '../../util/transform';
import { isEmail, isUUID } from '../../util/validate';
import { DocType } from '../global/Doc.enum';
import Root from '../Root';
import Subscription from '../subscription/Subscription';
import { SubscriptionStatusType } from '../subscription/Subscription.enum';
import { OrganizationType } from './Organization.enum';
import type { IOrganization } from './Organization.interface';
import OrganizationDetails from './OrganizationDetails';
import OrganizationEnterprise from './OrganizationEnterprise';
import OrganizationExchange from './OrganizationExchange';
import OrganizationFeatures from './OrganizationFeatures';
import OrganizationAccess from './OrganizationAccess';
import OrganizationIntegrations from './OrganizationIntegrations';
import OrganizationPermissions from './OrganizationPermissions';
import OrganizationFinancialPositionBuffers from './OrganizationFinancialPositionBuffers';
import OrganizationCommissionLimits from './OrganizationCommissionLimits';
import OrganizationWebHooks from './OrganizationWebHooks';

export default class Organization extends Root implements IOrganization {
  idRoot: string;
  idParent: string;
  idManagers: Array<string>;
  idAdministrators: Array<string>;
  idBrokers: Array<string>;
  idMentors: Array<string>;
  apiKeyHash: string;
  apiKeyDigest: string;
  internalReferenceId: string;
  externalReferenceId: string;
  organization: OrganizationDetails;
  enterprise: OrganizationEnterprise;
  features: OrganizationFeatures;
  access: [OrganizationAccess];
  integrations: OrganizationIntegrations;
  permissions: OrganizationPermissions;
  financialPositionBuffers: OrganizationFinancialPositionBuffers;
  commissionLimits: OrganizationCommissionLimits;
  exchange: OrganizationExchange;
  webHooks: OrganizationWebHooks;
  mentees: any;
  partnersAttributes: any;
  subscription: Subscription;

  // region Generic methods
  getType() {
    if (this.isRoot()) {
      return OrganizationType.root.title();
    }
    if (this.isGroup()) {
      return OrganizationType.group.title();
    }
    if (this.isBranch()) {
      return OrganizationType.branch.title();
    }
    return OrganizationType.orphan.title();
  }

  isRoot() {
    return !isUUID(this.idParent) && !isUUID(this.idRoot);
  }

  isGroup() {
    return !isUUID(this.idParent) && isUUID(this.idRoot);
  }

  isBranch() {
    return isUUID(this.idParent) && isUUID(this.idRoot);
  }

  getInternalReferenceId() {
    return this.internalReferenceId;
  }

  getExternalReferenceId() {
    return this.externalReferenceId;
  }
  // endregion Generic methods

  // region Organization Details
  getName() {
    return this.organization?.getName();
  }

  getNameExchanged() {
    return this.exchange?.name || this.organization?.getName();
  }

  getUrl() {
    return this.organization?.getUrl();
  }

  getLogo() {
    return this.organization?.getLogo();
  }

  getLogoClient() {
    return this.organization?.getLogoClient();
  }

  getLocation() {
    return this.organization?.getLocation();
  }
  // endregion Organization Details

  // region Organization Exchange
  hasEmail() {
    return isEmail(this.exchange?.eMail?.address);
  }
  // endregion Organization Exchange

  // region Organization Feature/Integration COMMS
  hasFeatureComms() {
    return this.features?.hasComms() && (this.isRoot() || this.enterprise?.features?.connect);
  }

  hasComms() {
    return this.hasFeatureComms() && this.integrations?.comms?.isEnabled();
  }

  hasCommsSms() {
    return this.hasFeatureComms() && this.integrations?.comms?.isEnabledSms();
  }

  hasCommsSmsInStock() {
    return this.hasCommsSms() && this.features?.configSMS?.messagesRemaining > 1;
  }

  canUseSMS() {
    return this.hasCommsSmsInStock();
  }

  hasCommsPhones() {
    return this.hasFeatureComms() && this.integrations?.comms?.isEnabledPhone();
  }

  hasCommsPhonesInStock() {
    return this.hasCommsPhones() && this.integrations?.comms?.hasPhonesInStock();
  }

  canUsePhone() {
    return (
      this.hasCommsPhonesInStock() &&
      (!this.isSubscribed() || (this.isSubscribed() && !this.isPhoneExpired()))
    );
  }

  getPhonesForOwner(idOwner) {
    return this.integrations?.comms?.getPhonesForOwner(idOwner);
  }
  // endregion Organization Feature/Integration COMMS

  // region Organization Feature/Integration ID Smart
  hasFeatureIdSmart() {
    return this.features.hasIdSmart();
  }

  hasIdSmartInStock() {
    return this.hasFeatureIdSmart() && this.features?.configIdSmart?.idSmartRemaining > 1;
  }

  canUseIdSmart() {
    return this.hasIdSmartInStock();
  }
  // endregion Organization Feature/Integration ID Smart

  // region Organization Feature/Integration Basiq
  hasFeatureBasiq() {
    return this.features.hasBasiq();
  }

  hasBasiqInStock() {
    return this.hasFeatureBasiq() && this.features?.configBasiq?.basiqRemaining > 1;
  }

  canUseBasiq() {
    return this.hasBasiqInStock();
  }
  // endregion Organization Feature/Integration Basiq

  // region Organization Feature/Integration PPSR
  hasFeaturePpsr() {
    return this.features.hasPpsr();
  }

  hasPpsrInStock() {
    return this.hasFeaturePpsr() && this.features?.configPpsr?.ppsrRemaining > 1;
  }

  canUsePpsr() {
    return this.hasPpsrInStock();
  }
  // endregion Organization Feature/Integration PPSR

  // region Organization Feature/Integration Diversification Opportunities
  hasFeatureDiversification() {
    return (
      this.features?.hasDiversification() &&
      (this.isRoot() || this.enterprise?.features?.diversification)
    );
  }
  // endregion Organization Feature/Integration Diversification Opportunities

  // region Organization Feature/Integration MARKETING
  hasFeatureMarketing() {
    return (
      this.features?.hasMarketing() || this.isRoot() || this.enterprise?.features?.marketing
    );
  }
  // endregion Organization Feature/Integration MARKETING

  // region Organization Feature/Integration CLIENT PORTAL
  hasFeatureClientPortal() {
    return (
      this.hasFeatureComms() &&
      this.features?.hasClientPortal() &&
      (this.isRoot() || this.enterprise?.features?.clientPortal)
    );
  }
  // endregion Organization Feature/Integration CLIENT PORTAL

  // region Organization Feature/Integration PARTNER PORTAL
  hasFeaturePartnerPortal() {
    return (
      this.features?.hasPartnerPortal() &&
      (this.isRoot() || this.enterprise?.features?.partnerPortal)
    );
  }
  // endregion Organization Feature/Integration PARTNER PORTAL

  // region Organization Feature/Integration EDOC SIGNATURES
  hasFeatureEDocSignatures() {
    return this.features?.hasEDocSignatures();
  }

  hasEDocSignaturesInStock() {
    return (
      this.hasFeatureEDocSignatures() &&
      this.features?.configEDocSignatures?.signaturesRemaining > 1
    );
  }

  canUseEDocSignatures() {
    return this.hasEDocSignaturesInStock();
  }
  // endregion Organization Feature/Integration EDOC SIGNATURES

  // region Organization Feature/Integration VIDEO CONFERENCING
  hasFeatureVideoConferencing() {
    return this.features?.hasVideoConferencing();
  }
  // endregion Organization Feature/Integration VIDEO CONFERENCING

  // region Organization Feature/Integration LOANQ
  isLoanQEnabled() {
    return this.integrations?.loanQ?.isEnabled();
  }
  // endregion Organization Feature/Integration MAIL CHIMP

  // region Organization Feature/Integration MAIL CHIMP
  hasMailChimpIntegration() {
    return this.hasEmail() && this.hasFeatureMarketing() && this.isMailChimpEnabled();
  }

  isMailChimpEnabled() {
    return this.integrations?.mailChimp?.isEnabled();
  }

  isMailChimpAutoSyncEnabled() {
    return this.integrations?.mailChimp?.isEnabledAutoSync();
  }
  // endregion Organization Feature/Integration MAIL CHIMP

  // region Organization Feature/Integration CAMPAIGN MONITOR
  hasCampaignMonitorIntegration() {
    return this.hasEmail() && this.hasFeatureMarketing() && this.isCampaignMonitorEnabled();
  }

  isCampaignMonitorEnabled() {
    return this.integrations?.campaignMonitor?.isEnabled();
  }

  isCampaignMonitorAutoSyncEnabled() {
    return this.integrations?.campaignMonitor?.isEnabledAutoSync();
  }

  isCampaignMonitorEmbedEnabled() {
    return this.integrations?.campaignMonitor?.hasAuthenticationEmbed();
  }
  // endregion Organization Feature/Integration CAMPAIGN MONITOR

  // region Organization Financial Position Buffers
  hasPrimaryIncomeBufferAF() {
    return this.financialPositionBuffers.hasPrimaryIncomeBufferAF();
  }

  hasPrimaryIncomeBufferHL() {
    return this.financialPositionBuffers.hasPrimaryIncomeBufferHL();
  }

  hasPrimaryExistingRepaymentBufferAF() {
    return this.financialPositionBuffers.hasPrimaryExistingRepaymentBufferAF();
  }

  hasPrimaryExistingRepaymentBufferHL() {
    return this.financialPositionBuffers.hasPrimaryExistingRepaymentBufferHL();
  }

  hasPrimaryProposedRepaymentBufferAF() {
    return this.financialPositionBuffers.hasPrimaryProposedRepaymentBufferAF();
  }

  hasPrimaryProposedRepaymentBufferHL() {
    return this.financialPositionBuffers.hasPrimaryProposedRepaymentBufferHL();
  }
  // endregion Organization Financial Position Buffers

  // region Organization Subscription
  isSubscribed() {
    return this.subscription?.hasSubscription()
      ? this.subscription?.getSubscription().isActive()
      : false;
  }

  isCancelled() {
    return this.subscription?.hasSubscription()
      ? this.subscription?.getSubscription().isCancelled()
      : false;
  }

  isExpired() {
    return this.subscription?.hasSubscription()
      ? this.subscription?.getSubscription().isStatusInactive()
      : false;
  }

  getSubscriptionPlanId() {
    return this.subscription?.hasSubscription()
      ? this.subscription?.getSubscription().type?.name
      : undefined;
  }

  getSubscriptionStatus() {
    if (this.subscription?.hasSubscription()) {
      return this.subscription?.getSubscription().getStatusTitle();
    }
    if (!this.subscription?.hasSubscription() && !this.isTrialExpired()) {
      return SubscriptionStatusType.trialing.title();
    }
    return undefined;
  }

  isBrokerSubscribed() {
    return this.subscription?.hasSubscriptionBrokers()
      ? this.subscription?.getSubscriptionBrokers().isActive()
      : false;
  }

  isBrokerExpired() {
    return this.subscription?.hasSubscriptionBrokers()
      ? this.subscription?.getSubscriptionBrokers().isStatusInactive()
      : false;
  }

  getBrokerSubscriptionStatus() {
    return this.subscription?.hasSubscriptionBrokers()
      ? this.subscription?.getSubscriptionBrokers().getStatusTitle()
      : SubscriptionStatusType.trialing.title();
  }

  isWebloanQSubscribed() {
    return this.subscription?.hasSubscriptionWebloanQ()
      ? this.subscription?.getSubscriptionWebloanQ().isActive()
      : false;
  }

  isWebloanQExpired() {
    return this.subscription?.hasSubscriptionWebloanQ()
      ? this.subscription?.getSubscriptionWebloanQ().isStatusInactive()
      : false;
  }

  getWebloanQSubscriptionStatus() {
    return this.subscription?.hasSubscriptionWebloanQ()
      ? this.subscription?.getSubscriptionWebloanQ().getStatusTitle()
      : SubscriptionStatusType.trialing.title();
  }

  isPartnerSubscribed() {
    return this.subscription?.hasSubscriptionPartners()
      ? this.subscription?.getSubscriptionPartners().isActive()
      : false;
  }

  isPartnerExpired() {
    return this.subscription?.hasSubscriptionPartners()
      ? this.subscription?.getSubscriptionPartners().isStatusInactive()
      : false;
  }

  getPartnerSubscriptionStatus() {
    return this.subscription?.hasSubscriptionPartners()
      ? this.subscription?.getSubscriptionPartners().getStatusTitle()
      : SubscriptionStatusType.trialing.title();
  }

  isPhoneSubscribed() {
    return this.subscription?.hasSubscriptionPhones()
      ? this.subscription?.getSubscriptionPhones().isActive()
      : false;
  }

  isPhoneExpired() {
    return this.subscription?.hasSubscriptionPhones()
      ? this.subscription?.getSubscriptionPhones().isStatusInactive()
      : false;
  }

  getPhoneSubscriptionStatus() {
    return this.subscription?.hasSubscriptionPhones()
      ? this.subscription?.getSubscriptionPhones().getStatusTitle()
      : false;
  }

  getTrialExpiration() {
    return future(utc(this.timeStamp), 'days', 60);
  }

  getTrialExpiresInDays() {
    return this.getTrialExpiration().diff(utc(), 'days').toObject().days;
  }

  getTrialExpirationDate(timeZone, locale, localeFormat) {
    return formatDate(tz(this.getTrialExpiration(), timeZone), locale, localeFormat);
  }

  getExpirationDate(timeZone, locale, localeFormat) {
    return this.subscription.hasSubscription()
      ? formatDate(
          tz(utc(this.subscription?.getSubscription().periodEnd), timeZone),
          locale,
          localeFormat
        )
      : 'N/A';
  }

  isTrialExpired() {
    return this.getTrialExpiration() < utc();
  }

  getAnyExpirationDate(timeZone, locale, localeFormat) {
    return this.isSubscribed()
      ? this.getExpirationDate(timeZone, locale, localeFormat)
      : this.getTrialExpirationDate(timeZone, locale, localeFormat);
  }

  isAnyExpired() {
    return this.isSubscribed() ? this.isExpired() : this.isTrialExpired();
  }

  isAnyBrokerExpired() {
    return this.isBrokerSubscribed()
      ? this.isBrokerExpired()
      : this.isTrialExpired() || this.isSubscribed();
  }

  isAnyWebloanQExpired() {
    return this.isWebloanQSubscribed() ? this.isWebloanQExpired() : this.isTrialExpired();
  }

  isAnyPartnerExpired() {
    return this.isPartnerSubscribed() ? this.isPartnerExpired() : this.isTrialExpired();
  }

  isAnyPhoneExpired() {
    return this.isPhoneSubscribed() ? this.isPhoneExpired() : this.isTrialExpired();
  }
  // endregion Organization Subscription

  // region Organization commission limits
  hasAggregatorCommissionSharedWithBrokers() {
    return this.commissionLimits?.assetFinance?.isSharedWithBrokers || false;
  }
  // endregion Organization commission limits

  static build = (data: IOrganization) => new Organization(data);
  static blank = () => new Organization({ isActive: true });

  /**
   * Constructor
   * @param {IOrganization} data
   */
  constructor(data?: IOrganization) {
    super(DocType.of('__global.organization'), data);
    this.idRoot = toUUID(data?.idRoot);
    this.idParent = toUUID(data?.idParent);
    this.idManagers = compact(map(data?.idManagers, (obj) => toUUID(obj)));
    this.idAdministrators = compact(map(data?.idAdministrators, (obj) => toUUID(obj)));
    this.idBrokers = compact(map(data?.idBrokers, (obj) => toUUID(obj)));
    this.idMentors = compact(map(data?.idMentors, (obj) => toUUID(obj)));
    this.apiKeyHash = data?.apiKeyHash || undefined;
    this.apiKeyDigest = data?.apiKeyDigest || undefined;
    this.internalReferenceId = data?.internalReferenceId || undefined;
    this.externalReferenceId = data?.externalReferenceId || undefined;
    this.organization = new OrganizationDetails(data?.organization);
    this.enterprise = new OrganizationEnterprise(data?.enterprise);
    this.features = new OrganizationFeatures(data?.features);
    this.access = compact(map(data?.access, (obj) => new OrganizationAccess(obj)));
    this.integrations = new OrganizationIntegrations(data?.integrations);
    this.permissions = new OrganizationPermissions(data?.permissions);
    this.financialPositionBuffers = new OrganizationFinancialPositionBuffers(
      data?.financialPositionBuffers
    );
    this.commissionLimits = new OrganizationCommissionLimits(data?.commissionLimits);
    this.exchange = new OrganizationExchange(data?.exchange);
    this.webHooks = new OrganizationWebHooks(data?.webHooks);
    this.mentees = data?.mentees || undefined;
    this.partnersAttributes = data?.partnersAttributes || {};
    this.subscription = new Subscription(data?.subscription);
  }
}
